import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { adminApi, auth, API_BASE } from '@/lib/api';
import { formatDate } from '@/lib/taxonomies';
import { ADMIN } from '@/constants/testIds';
import { useSeo } from '@/lib/seo';

export default function SubscribersPage() {
  useSeo({ title: 'Subscribers | Admin' });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminApi.subscribers().then(setRows).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const download = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/subscribers.csv`, {
        headers: { Authorization: `Bearer ${auth.getToken()}` },
      });
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'subscribers.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      await adminApi.deleteSubscriber(id);
      toast.success('Removed');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="kicker">Subscribers</p>
          <h1 className="font-serif text-3xl tracking-tight">The Field Report list.</h1>
        </div>
        <Button variant="secondary" onClick={download} data-testid={ADMIN.subscribersExport}>Export CSV</Button>
      </div>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>First name</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right pr-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={4} className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]">Loading…</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center py-10">No subscribers yet.</TableCell></TableRow>
              )}
              {!loading && rows.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.email}</TableCell>
                  <TableCell>{s.first_name || '—'}</TableCell>
                  <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">{formatDate(s.created_at)}</TableCell>
                  <TableCell className="text-right pr-6">
                    <Button variant="ghost" size="sm" onClick={() => remove(s.id)}>Remove</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
